export const PLAYER_IDS = ["player-1", "player-2"] as const;
export type PlayerId = (typeof PLAYER_IDS)[number];

export const LANE_IDS = ["left", "center", "right"] as const;
export type LaneId = (typeof LANE_IDS)[number];

/** The owner-approved Core Alpha rules revision introduced formation Flanks. */
export const FLANK_KINGDOM_COMPLETE_RULES_VERSION =
  "first-edition-flank-kingdom-complete-alpha" as const;
/** Owner-approved public-beta action-window, hand-limit, Army, and Event rules. */
export const PUBLIC_BETA_RULES_VERSION = "public-beta-action-window-v1" as const;
export const MAX_HAND_SIZE = 10 as const;

export const GAME_PHASES = [
  "ready",
  "draw",
  "land",
  "supply",
  "command",
  "battle",
  "cleanup",
  "action-window",
  "finished",
] as const;
export type GamePhase = (typeof GAME_PHASES)[number];

export type CardInstanceId = string;
export type CardDefinitionId = string;
export type LandTypeId = string;

export type CardType = "land" | "commander" | "army" | "event" | "policy" | "conspiracy";

export interface EngineLandStageDefinitionV1 {
  readonly stage: 1 | 2 | 3;
  readonly maximumControl: number;
}

export interface EngineLandCardDefinitionV1 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "land";
  readonly developmentId: CardDefinitionId;
  readonly stages: readonly [
    EngineLandStageDefinitionV1,
    EngineLandStageDefinitionV1,
    EngineLandStageDefinitionV1,
  ];
}

export interface EngineNonLandCardDefinitionV1 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: Exclude<CardType, "land">;
}

export type EngineCardDefinitionV1 = EngineLandCardDefinitionV1 | EngineNonLandCardDefinitionV1;

export interface EngineLandStageDefinition {
  readonly stage: 1 | 2 | 3;
  readonly maximumControl: number;
  readonly production: ResourcePool;
}

export interface EngineLandCardDefinition {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "land";
  readonly landTypeId: LandTypeId;
  readonly developmentId: CardDefinitionId;
  readonly stages: readonly [
    EngineLandStageDefinition,
    EngineLandStageDefinition,
    EngineLandStageDefinition,
  ];
}

export interface EngineArmyCardDefinition {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "army";
  readonly armySize: number;
  readonly attack: number;
  readonly guard: number;
  readonly upkeep: ResourcePool;
}

export interface EngineNonLandCardDefinition {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: Exclude<CardType, "land" | "army">;
}

export type EngineCardDefinitionV2 =
  EngineLandCardDefinition | EngineArmyCardDefinition | EngineNonLandCardDefinition;

export interface EngineMandateReference {
  readonly mandateId: string;
  readonly name: string;
  readonly condition: { readonly conditionId: string };
  readonly effects: readonly { readonly effectId: string }[];
}

export interface EngineLandCardDefinitionV3 extends EngineLandCardDefinition {
  readonly cost: ResourcePool;
}

export interface EngineArmyCardDefinitionV3 extends EngineArmyCardDefinition {
  readonly cost: ResourcePool;
}

export interface EngineCommanderCardDefinitionV3 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "commander";
  readonly cost: ResourcePool;
  readonly attack: number;
  readonly guard: number;
  readonly armyCapacity: number;
  readonly mandates: readonly EngineMandateReference[];
}

export interface EngineOtherCardDefinitionV3 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: Exclude<CardType, "land" | "army" | "commander">;
  readonly cost: ResourcePool;
}

export type EngineCardDefinitionV3 =
  | EngineLandCardDefinitionV3
  | EngineArmyCardDefinitionV3
  | EngineCommanderCardDefinitionV3
  | EngineOtherCardDefinitionV3;

export interface EngineCommanderCardDefinitionV4 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "commander";
  readonly cost: ResourcePool;
  readonly attack: number;
  readonly guard: number;
  readonly mandates: readonly EngineMandateReference[];
}

export type EngineEventEffectV1 =
  | {
      readonly type: "draw-cards";
      readonly count: number;
    }
  | {
      readonly type: "gain-kingdom-resources";
      readonly resources: ResourcePool;
    };

export interface EngineEventCardDefinitionV4 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "event";
  readonly cost: ResourcePool;
  readonly effects: readonly EngineEventEffectV1[];
}

export interface EngineOtherCardDefinitionV4 {
  readonly id: CardDefinitionId;
  readonly name: string;
  readonly type: "policy" | "conspiracy";
  readonly cost: ResourcePool;
}

export type EngineCardDefinitionV4 =
  | EngineLandCardDefinitionV3
  | EngineArmyCardDefinitionV3
  | EngineCommanderCardDefinitionV4
  | EngineEventCardDefinitionV4
  | EngineOtherCardDefinitionV4;

export type EngineCardDefinition =
  EngineCardDefinitionV1 | EngineCardDefinitionV2 | EngineCardDefinitionV3 | EngineCardDefinitionV4;

/**
 * A plain, validated card-data projection. It intentionally excludes Zod and
 * presentation metadata while retaining the definitions this engine slice can
 * authoritatively resolve.
 */
export interface EngineCardCatalogV1 {
  readonly schemaVersion: 1;
  readonly cardDataVersion: string;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV1[];
}

export interface EngineCardCatalogV2 {
  readonly schemaVersion: 2;
  readonly cardDataVersion: string;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV2[];
}

export interface EngineCardCatalogV3 {
  readonly schemaVersion: 3;
  readonly cardDataVersion: string;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV3[];
}

export interface EngineCardCatalogV4 {
  readonly schemaVersion: 4;
  readonly cardDataVersion: string;
  readonly gameplayCatalogHash: string;
  readonly cards: readonly EngineCardDefinitionV4[];
}

export type EngineCardCatalog =
  EngineCardCatalogV1 | EngineCardCatalogV2 | EngineCardCatalogV3 | EngineCardCatalogV4;

export interface MatchDeckInput {
  readonly deckId: string;
  /** Original, unshuffled definition order. Each physical copy has one entry. */
  readonly cardDefinitionIds: readonly CardDefinitionId[];
}

export interface MatchSetupInput {
  readonly matchId: string;
  readonly seed: string;
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
  readonly gameplayCatalogHash: string;
  readonly catalog: EngineCardCatalog;
  readonly decks: Readonly<Record<PlayerId, MatchDeckInput>>;
}

export interface CardInstanceRecord {
  readonly cardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly ownerPlayerId: PlayerId;
  readonly originalDeckOrdinal: number;
}

export interface OpeningHandAudit {
  readonly acceptedCardInstanceIds: readonly CardInstanceId[];
  readonly drawAttempts: number;
  readonly rejectedHands: readonly (readonly CardInstanceId[])[];
}

export type FirstTurnDrawStatus = "pending" | "consumed";

export interface MatchSetupMetadata {
  readonly gameplayCatalogHash: string;
  readonly startingPlayerId: PlayerId;
  readonly initialRandomState: SeededRandomState;
  readonly finalSetupRandomState: SeededRandomState;
  readonly openingHands: Readonly<Record<PlayerId, OpeningHandAudit>>;
}

export type CardZone =
  "deck" | "hand" | "discard" | "exhaust" | "event-resolution" | "battlefield" | "committed";

export interface ResourcePool {
  readonly food: number;
  readonly production: number;
  readonly trade: number;
  readonly science: number;
  readonly culture: number;
}

export interface TurnActionUsage {
  readonly landActions: number;
  readonly deploymentActions: number;
  readonly governanceActions: number;
  readonly eventsPlayed: number;
  readonly eventAuthorizationLanesUsed: readonly LaneId[];
  readonly attacksMadeFromLanes: readonly LaneId[];
}

export type CommanderReadiness = "ready" | "spent";
export type LaneSideStatus = "empty" | "active" | "defeated" | "committed";
export type LaneResult = "active" | "player-1-won" | "player-2-won";

export interface LandPermanent {
  readonly cardInstanceIds: readonly CardInstanceId[];
  readonly stage: 1 | 2 | 3;
  readonly currentControl: number;
  readonly maximumControl: number;
  readonly attackBonus: number;
  readonly guardBonus: number;
}

export interface CommanderPermanent {
  readonly cardInstanceId: CardInstanceId;
  readonly attack: number;
  readonly guard: number;
  /** Historical V1-V4 printed value. Public-beta V5 deliberately omits capacity. */
  readonly armyCapacity?: number;
  readonly readiness: CommanderReadiness;
  readonly deployedThisTurn: boolean;
  readonly mayAttackOnDeploymentTurn: boolean;
}

export interface ArmyPermanent {
  readonly cardInstanceId: CardInstanceId;
  readonly armySize: number;
  readonly attack: number;
  readonly guard: number;
}

/**
 * A resolved, authoritative modifier. Card-effect evaluation is intentionally
 * outside the first slice; combat never accepts a client-supplied total.
 */
export interface CombatModifier {
  readonly modifierId: string;
  readonly sourceCardInstanceId: CardInstanceId | null;
  readonly attack: number;
  readonly guard: number;
}

export interface PlayerLaneState {
  readonly status: LaneSideStatus;
  readonly land: LandPermanent | null;
  readonly commander: CommanderPermanent | null;
  readonly armies: readonly ArmyPermanent[];
  readonly conspiracyCardInstanceId: CardInstanceId | null;
  readonly attachedCardInstanceIds: readonly CardInstanceId[];
  readonly combatModifiers: readonly CombatModifier[];
}

export interface LaneState {
  readonly laneId: LaneId;
  readonly players: Readonly<Record<PlayerId, PlayerLaneState>>;
  readonly result: LaneResult;
}

export interface PlayerState {
  readonly playerId: PlayerId;
  readonly deck: readonly CardInstanceId[];
  readonly hand: readonly CardInstanceId[];
  readonly discard: readonly CardInstanceId[];
  readonly exhaust: readonly CardInstanceId[];
  readonly eventResolution: readonly CardInstanceId[];
  readonly activePolicyId: CardInstanceId | null;
  readonly victoryBanners: number;
  readonly actionsUsed: TurnActionUsage;
  readonly kingdomPool: ResourcePool;
}

export interface KingdomProgress {
  /** Canonical `LANE_IDS` order; founding is historical rather than territorial control. */
  readonly populatedLaneIds: readonly LaneId[];
  readonly complete: boolean;
}

export interface NoTerritoryWarning {
  readonly threatenedPlayerId: PlayerId;
  readonly threateningPlayerId: PlayerId;
  readonly createdActionSequence: number;
  readonly createdTurnNumber: number;
  /** The threatened player's next Land Phase whose entry starts after creation. */
  readonly deadlineTurnNumber: number | null;
}

export type SupplyStatus =
  | { readonly type: "not-started" }
  | {
      readonly type: "awaiting-upkeep-discard";
      readonly playerId: PlayerId;
      readonly turnNumber: number;
    }
  | { readonly type: "resolved"; readonly playerId: PlayerId; readonly turnNumber: number };

export interface LaneSupplySnapshot {
  readonly laneId: LaneId;
  readonly active: boolean;
  readonly landTypeId: LandTypeId | null;
  readonly landStage: 1 | 2 | 3 | null;
  readonly production: ResourcePool;
  readonly upkeep: ResourcePool;
  readonly shortfall: ResourcePool;
  readonly remainder: ResourcePool;
  readonly sustainable: boolean;
  readonly armyCardInstanceIds: readonly CardInstanceId[];
  readonly discardableArmyCardInstanceIds: readonly CardInstanceId[];
}

export interface SupplySnapshot {
  readonly playerId: PlayerId;
  readonly status: SupplyStatus;
  readonly lanes: Readonly<Record<LaneId, LaneSupplySnapshot>>;
  readonly unsustainableLaneIds: readonly LaneId[];
  /** Zero until every lane is sustainable, because transfer is atomic. */
  readonly kingdomPoolContribution: ResourcePool;
}

export interface ActiveMatchStatus {
  readonly type: "active";
}

export interface FinishedMatchStatus {
  readonly type: "finished";
  readonly winnerPlayerId: PlayerId;
  readonly reason: "two-victory-banners" | "no-territory";
}

export type MatchStatus = ActiveMatchStatus | FinishedMatchStatus;

export interface SeededRandomState {
  readonly algorithm: "xorshift32";
  readonly seed: string;
  readonly value: number;
  readonly draws: number;
}

export interface AdvancePhaseAction {
  readonly type: "advance-phase";
  readonly playerId: PlayerId;
}

export interface AttackAction {
  readonly type: "attack";
  readonly playerId: PlayerId;
  /** Source lane. Historical actions target this same lane when `targetLaneId` is absent. */
  readonly laneId: LaneId;
  readonly targetLaneId?: LaneId;
}

export interface FoundLandAction {
  readonly type: "found-land";
  readonly playerId: PlayerId;
  readonly cardInstanceId: CardInstanceId;
  readonly laneId: LaneId;
}

export interface DevelopLandAction {
  readonly type: "develop-land";
  readonly playerId: PlayerId;
  readonly cardInstanceId: CardInstanceId;
  readonly laneId: LaneId;
}

export interface DiscardArmyForUpkeepAction {
  readonly type: "discard-army-for-upkeep";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
}

export interface DisbandArmyAction {
  readonly type: "disband-army";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
}

export interface DisbandCommanderAction {
  readonly type: "disband-commander";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
}

export interface DeployCommanderAction {
  readonly type: "deploy-commander";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
}

export interface DeployArmyAction {
  readonly type: "deploy-army";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
}

export interface ReplaceCommanderAction {
  readonly type: "replace-commander";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
}

export interface PlayEventAction {
  readonly type: "play-event";
  readonly playerId: PlayerId;
  readonly cardInstanceId: CardInstanceId;
}

export interface EndTurnAction {
  readonly type: "end-turn";
  readonly playerId: PlayerId;
}

export type DeferredActionKind =
  | "match-setup"
  | "draw-cards"
  | "land-action"
  | "supply-resolution"
  | "deployment-action"
  | "governance-action"
  | "play-event"
  | "disband-card"
  | "resolve-card-effect";

/** A named boundary for rules that are not yet implemented. */
export interface DeferredGameAction {
  readonly type: "deferred";
  readonly playerId: PlayerId;
  readonly actionKind: DeferredActionKind;
}

export type GameAction =
  | AdvancePhaseAction
  | AttackAction
  | FoundLandAction
  | DevelopLandAction
  | DiscardArmyForUpkeepAction
  | DisbandArmyAction
  | DisbandCommanderAction
  | DeployCommanderAction
  | DeployArmyAction
  | ReplaceCommanderAction
  | PlayEventAction
  | EndTurnAction
  | DeferredGameAction;

export interface GameActionRecord {
  readonly sequence: number;
  readonly playerId: PlayerId;
  readonly action: GameAction;
}

interface BaseGameEvent {
  readonly eventId: string;
  readonly actionSequence: number;
}

export interface MatchSetupStartedEvent extends BaseGameEvent {
  readonly type: "match-setup-started";
  readonly matchId: string;
  readonly seed: string;
  readonly gameplayCatalogHash: string;
}

export interface DeckInstantiatedEvent extends BaseGameEvent {
  readonly type: "deck-instantiated";
  readonly playerId: PlayerId;
  readonly deckId: string;
  readonly cardInstanceIds: readonly CardInstanceId[];
}

export interface DeckShuffledEvent extends BaseGameEvent {
  readonly type: "deck-shuffled";
  readonly playerId: PlayerId;
  readonly purpose: "opening-hand" | "opening-hand-redraw" | "discard-recycle";
  readonly attempt: number;
  readonly cardInstanceIds: readonly CardInstanceId[];
}

export interface OpeningHandDrawnEvent extends BaseGameEvent {
  readonly type: "opening-hand-drawn";
  readonly playerId: PlayerId;
  readonly attempt: number;
  readonly cardInstanceIds: readonly CardInstanceId[];
}

export interface OpeningHandRejectedEvent extends BaseGameEvent {
  readonly type: "opening-hand-rejected";
  readonly playerId: PlayerId;
  readonly attempt: number;
  readonly cardInstanceIds: readonly CardInstanceId[];
  readonly reason: "no-land";
}

export interface OpeningHandReturnedEvent extends BaseGameEvent {
  readonly type: "opening-hand-returned";
  readonly playerId: PlayerId;
  readonly attempt: number;
  readonly cardInstanceIds: readonly CardInstanceId[];
}

export interface StartingPlayerSelectedEvent extends BaseGameEvent {
  readonly type: "starting-player-selected";
  readonly playerId: PlayerId;
}

export interface MatchCreatedEvent extends BaseGameEvent {
  readonly type: "match-created";
  readonly matchId: string;
  readonly startingPlayerId: PlayerId;
  readonly randomDraws: number;
}

export interface DrawSkippedEvent extends BaseGameEvent {
  readonly type: "draw-skipped";
  readonly playerId: PlayerId;
  readonly reason: "opening-hand-used-for-first-turn";
}

export interface CardsDrawnEvent extends BaseGameEvent {
  readonly type: "cards-drawn";
  readonly playerId: PlayerId;
  readonly requestedCount: number;
  readonly cardInstanceIds: readonly CardInstanceId[];
}

export interface DeckRecycledEvent extends BaseGameEvent {
  readonly type: "deck-recycled";
  readonly playerId: PlayerId;
  readonly cardInstanceIds: readonly CardInstanceId[];
}

export interface DrawShortfallEvent extends BaseGameEvent {
  readonly type: "draw-shortfall";
  readonly playerId: PlayerId;
  readonly requestedCount: number;
  readonly drawnCount: number;
}

export interface HandLimitReachedEvent extends BaseGameEvent {
  readonly type: "hand-limit-reached";
  readonly playerId: PlayerId;
  readonly requestedCount: number;
  readonly drawableCount: number;
  readonly handSizeBefore: number;
  readonly maximumHandSize: typeof MAX_HAND_SIZE;
}

export interface LandFoundedEvent extends BaseGameEvent {
  readonly type: "land-founded";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly cardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly stage: 1;
  readonly currentControl: number;
  readonly maximumControl: number;
}

export interface LandDevelopedEvent extends BaseGameEvent {
  readonly type: "land-developed";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly cardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly previousStage: 1 | 2;
  readonly stage: 2 | 3;
  readonly previousCurrentControl: number;
  readonly currentControl: number;
  readonly previousMaximumControl: number;
  readonly maximumControl: number;
}

export interface LandActionUsedEvent extends BaseGameEvent {
  readonly type: "land-action-used";
  readonly playerId: PlayerId;
  readonly actionType: "found-land" | "develop-land";
  readonly landActionsUsed: 1;
}

export interface KingdomProgressUpdatedEvent extends BaseGameEvent {
  readonly type: "kingdom-progress-updated";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly populatedLaneIds: readonly LaneId[];
  readonly complete: boolean;
}

export interface KingdomCompletedEvent extends BaseGameEvent {
  readonly type: "kingdom-completed";
  readonly playerId: PlayerId;
}

export interface NoTerritoryWarningStartedEvent extends BaseGameEvent {
  readonly type: "no-territory-warning-started";
  readonly threatenedPlayerId: PlayerId;
  readonly threateningPlayerId: PlayerId;
}

export interface NoTerritoryWarningClearedEvent extends BaseGameEvent {
  readonly type: "no-territory-warning-cleared";
  readonly threatenedPlayerId: PlayerId;
  readonly reason: "territory-restored" | "threatening-player-lost-territory" | "match-finished";
}

export interface SupplyStartedEvent extends BaseGameEvent {
  readonly type: "supply-started";
  readonly playerId: PlayerId;
  readonly turnNumber: number;
}

export interface LandProductionCalculatedEvent extends BaseGameEvent {
  readonly type: "land-production-calculated";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly landTypeId: LandTypeId;
  readonly landStage: 1 | 2 | 3;
  readonly production: ResourcePool;
  readonly upkeep: ResourcePool;
  readonly shortfall: ResourcePool;
  readonly remainder: ResourcePool;
  readonly armyCardInstanceIds: readonly CardInstanceId[];
}

export interface UpkeepShortfallDetectedEvent extends BaseGameEvent {
  readonly type: "upkeep-shortfall-detected";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly shortfall: ResourcePool;
  readonly armyCardInstanceIds: readonly CardInstanceId[];
}

export interface ArmyDiscardedForUpkeepEvent extends BaseGameEvent {
  readonly type: "army-discarded-for-upkeep";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
  readonly armyCardDefinitionId: CardDefinitionId;
}

export interface LocalUpkeepPaidEvent extends BaseGameEvent {
  readonly type: "local-upkeep-paid";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly production: ResourcePool;
  readonly upkeep: ResourcePool;
  readonly remainder: ResourcePool;
}

export interface KingdomPoolIncreasedEvent extends BaseGameEvent {
  readonly type: "kingdom-pool-increased";
  readonly playerId: PlayerId;
  readonly contribution: ResourcePool;
  readonly kingdomPool: ResourcePool;
}

export interface SupplyResolvedEvent extends BaseGameEvent {
  readonly type: "supply-resolved";
  readonly playerId: PlayerId;
  readonly turnNumber: number;
  readonly kingdomPool: ResourcePool;
}

export interface ArmyDisbandedEvent extends BaseGameEvent {
  readonly type: "army-disbanded";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly destination: "discard";
  readonly remainingArmyCardInstanceIds: readonly CardInstanceId[];
}

export interface CommanderDisbandedEvent extends BaseGameEvent {
  readonly type: "commander-disbanded";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly destination: "discard";
  readonly remainingArmyCardInstanceIds: readonly CardInstanceId[];
  readonly eventLimitBefore: number;
  readonly eventLimitAfter: number;
}

export interface KingdomPoolSpentEvent extends BaseGameEvent {
  readonly type: "kingdom-pool-spent";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly reason: "deploy-commander" | "replace-commander";
  readonly costPaid: ResourcePool;
  readonly previousKingdomPool: ResourcePool;
  readonly remainingKingdomPool: ResourcePool;
}

/** Additive Army spending shape; historical Commander event payloads remain unchanged. */
export interface ArmyKingdomPoolSpentEvent extends BaseGameEvent {
  readonly type: "kingdom-pool-spent";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly reason: "deploy-army";
  readonly costPaid: ResourcePool;
  readonly previousKingdomPool: ResourcePool;
  readonly remainingKingdomPool: ResourcePool;
}

export interface ArmyDeployedEvent extends BaseGameEvent {
  readonly type: "army-deployed";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly armyCardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly armySize: number;
  readonly printedAttack: number;
  readonly printedGuard: number;
  readonly printedUpkeep: ResourcePool;
  readonly costPaid: ResourcePool;
  readonly remainingKingdomPool: ResourcePool;
  readonly armyCountBefore: number;
  readonly armyCountAfter: number;
  readonly totalArmySizeBefore: number;
  readonly totalArmySizeAfter: number;
  /** Historical V1-V4 diagnostic. Public-beta legality is card-count based. */
  readonly commanderCapacity?: number;
  readonly projectedNextSupplySustainable: boolean;
}

export interface CommanderDeployedEvent extends BaseGameEvent {
  readonly type: "commander-deployed";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly printedAttack: number;
  readonly printedGuard: number;
  readonly armyCapacity?: number;
  readonly costPaid: ResourcePool;
  readonly remainingKingdomPool: ResourcePool;
  readonly garrisonArmyCardInstanceIds: readonly CardInstanceId[];
  readonly eventLimitBefore: number;
  readonly eventLimitAfter: number;
}

export interface CommanderReplacedEvent extends BaseGameEvent {
  readonly type: "commander-replaced";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly oldCommanderCardInstanceId: CardInstanceId;
  readonly oldCommanderDefinitionId: CardDefinitionId;
  readonly newCommanderCardInstanceId: CardInstanceId;
  readonly newCommanderDefinitionId: CardDefinitionId;
  readonly oldCommanderDestination: "discard";
  readonly printedAttack: number;
  readonly printedGuard: number;
  readonly armyCapacity?: number;
  readonly costPaid: ResourcePool;
  readonly remainingKingdomPool: ResourcePool;
  readonly garrisonArmyCardInstanceIds: readonly CardInstanceId[];
  readonly eventLimitBefore: number;
  readonly eventLimitAfter: number;
  readonly authorizationLaneAlreadyUsed: boolean;
}

export interface DeploymentActionUsedEvent extends BaseGameEvent {
  readonly type: "deployment-action-used";
  readonly playerId: PlayerId;
  readonly actionType: "deploy-army" | "deploy-commander" | "replace-commander";
  readonly deploymentActionsUsed: 1;
}

export interface TurnStartedEvent extends BaseGameEvent {
  readonly type: "turn-started";
  readonly playerId: PlayerId;
  readonly turnNumber: number;
}

export interface ActionWindowOpenedEvent extends BaseGameEvent {
  readonly type: "action-window-opened";
  readonly playerId: PlayerId;
  readonly turnNumber: number;
}

export interface TurnHandedOffEvent extends BaseGameEvent {
  readonly type: "turn-handed-off";
  readonly previousPlayerId: PlayerId;
  readonly activePlayerId: PlayerId;
  readonly turnNumber: number;
}

export interface EventPlayedEvent extends BaseGameEvent {
  readonly type: "event-played";
  readonly playerId: PlayerId;
  readonly cardInstanceId: CardInstanceId;
  readonly cardDefinitionId: CardDefinitionId;
  readonly costPaid: ResourcePool;
  readonly eventLimit: number;
  readonly eventsPlayed: number;
}

export interface EventEffectResolvedEvent extends BaseGameEvent {
  readonly type: "event-effect-resolved";
  readonly playerId: PlayerId;
  readonly cardInstanceId: CardInstanceId;
  readonly effectIndex: number;
  readonly effect: EngineEventEffectV1;
}

export interface EventMovedToDiscardEvent extends BaseGameEvent {
  readonly type: "event-moved-to-discard";
  readonly playerId: PlayerId;
  readonly cardInstanceId: CardInstanceId;
}

export interface PhaseChangedEvent extends BaseGameEvent {
  readonly type: "phase-changed";
  readonly from: GamePhase;
  readonly to: GamePhase;
  readonly previousActivePlayerId: PlayerId;
  readonly activePlayerId: PlayerId;
  readonly turnNumber: number;
}

export interface CommanderReadiedEvent extends BaseGameEvent {
  readonly type: "commander-readied";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
}

export interface CleanupStartedEvent extends BaseGameEvent {
  readonly type: "cleanup-started";
  readonly playerId: PlayerId;
  readonly turnNumber: number;
}

export interface CleanupHandDiscardedEvent extends BaseGameEvent {
  readonly type: "cleanup-hand-discarded";
  readonly playerId: PlayerId;
  readonly cardInstanceIds: readonly CardInstanceId[];
  readonly handCountBefore: number;
  readonly handCountAfter: 0;
  readonly discardCountBefore: number;
  readonly discardCountAfter: number;
}

export interface TemporaryCombatModifiersClearedEvent extends BaseGameEvent {
  readonly type: "temporary-combat-modifiers-cleared";
  readonly playerId: PlayerId;
  readonly removals: readonly {
    readonly laneId: LaneId;
    readonly affectedPlayerId: PlayerId;
    readonly modifierIds: readonly string[];
  }[];
}

export interface EventAuthorizationClearedEvent extends BaseGameEvent {
  readonly type: "event-authorization-cleared";
  readonly playerId: PlayerId;
  readonly eventsPlayedBefore: number;
  readonly eventAuthorizationLanesUsedBefore: readonly LaneId[];
}

export interface KingdomPoolClearedEvent extends BaseGameEvent {
  readonly type: "kingdom-pool-cleared";
  readonly playerId: PlayerId;
  readonly clearedResources: ResourcePool;
  readonly resultingPool: ResourcePool;
}

export interface TurnActionUsageResetEvent extends BaseGameEvent {
  readonly type: "turn-action-usage-reset";
  readonly playerId: PlayerId;
  readonly previousActionUsage: TurnActionUsage;
  readonly actionUsage: TurnActionUsage;
}

export interface SupplyStatusResetEvent extends BaseGameEvent {
  readonly type: "supply-status-reset";
  readonly playerId: PlayerId;
  readonly previousSupplyStatus: SupplyStatus;
  readonly supplyStatus: { readonly type: "not-started" };
}

export interface CleanupCompletedEvent extends BaseGameEvent {
  readonly type: "cleanup-completed";
  readonly playerId: PlayerId;
  readonly nextActivePlayerId: PlayerId;
  readonly nextTurnNumber: number;
}

export interface AttackDeclaredEvent extends BaseGameEvent {
  readonly type: "attack-declared";
  readonly playerId: PlayerId;
  readonly defendingPlayerId: PlayerId;
  readonly laneId: LaneId;
  readonly sourceLaneId?: LaneId;
  readonly targetLaneId?: LaneId;
  readonly attackKind?: "direct" | "flank";
  readonly commanderCardInstanceId: CardInstanceId;
}

export interface CombatCalculatedEvent extends BaseGameEvent {
  readonly type: "combat-calculated";
  readonly playerId: PlayerId;
  readonly defendingPlayerId: PlayerId;
  readonly laneId: LaneId;
  readonly sourceLaneId?: LaneId;
  readonly targetLaneId?: LaneId;
  readonly attackKind?: "direct" | "flank";
  readonly totalAttack: number;
  readonly totalGuard: number;
  readonly damage: number;
}

export interface LandDamagedEvent extends BaseGameEvent {
  readonly type: "land-damaged";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly targetLaneId?: LaneId;
  readonly damage: number;
  readonly previousControl: number;
  readonly currentControl: number;
}

export interface LaneDefeatedEvent extends BaseGameEvent {
  readonly type: "lane-defeated";
  readonly laneId: LaneId;
  readonly winnerPlayerId: PlayerId;
  readonly defeatedPlayerId: PlayerId;
  readonly exhaustedCardInstanceIds: readonly CardInstanceId[];
}

export interface VictoryBannerAwardedEvent extends BaseGameEvent {
  readonly type: "victory-banner-awarded";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly victoryBanners: number;
}

export interface LaneCommittedEvent extends BaseGameEvent {
  readonly type: "lane-committed";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
}

export interface CommanderSpentEvent extends BaseGameEvent {
  readonly type: "commander-spent";
  readonly playerId: PlayerId;
  readonly laneId: LaneId;
  readonly commanderCardInstanceId: CardInstanceId;
}

export interface MatchFinishedEvent extends BaseGameEvent {
  readonly type: "match-finished";
  readonly winnerPlayerId: PlayerId;
  readonly reason: "two-victory-banners" | "no-territory";
}

export type GameEvent =
  | MatchSetupStartedEvent
  | DeckInstantiatedEvent
  | DeckShuffledEvent
  | OpeningHandDrawnEvent
  | OpeningHandRejectedEvent
  | OpeningHandReturnedEvent
  | StartingPlayerSelectedEvent
  | MatchCreatedEvent
  | DrawSkippedEvent
  | CardsDrawnEvent
  | DeckRecycledEvent
  | DrawShortfallEvent
  | HandLimitReachedEvent
  | LandFoundedEvent
  | LandDevelopedEvent
  | LandActionUsedEvent
  | KingdomProgressUpdatedEvent
  | KingdomCompletedEvent
  | NoTerritoryWarningStartedEvent
  | NoTerritoryWarningClearedEvent
  | SupplyStartedEvent
  | LandProductionCalculatedEvent
  | UpkeepShortfallDetectedEvent
  | ArmyDiscardedForUpkeepEvent
  | LocalUpkeepPaidEvent
  | KingdomPoolIncreasedEvent
  | SupplyResolvedEvent
  | ArmyDisbandedEvent
  | CommanderDisbandedEvent
  | KingdomPoolSpentEvent
  | ArmyKingdomPoolSpentEvent
  | ArmyDeployedEvent
  | CommanderDeployedEvent
  | CommanderReplacedEvent
  | DeploymentActionUsedEvent
  | TurnStartedEvent
  | ActionWindowOpenedEvent
  | TurnHandedOffEvent
  | EventPlayedEvent
  | EventEffectResolvedEvent
  | EventMovedToDiscardEvent
  | PhaseChangedEvent
  | CommanderReadiedEvent
  | CleanupStartedEvent
  | CleanupHandDiscardedEvent
  | TemporaryCombatModifiersClearedEvent
  | EventAuthorizationClearedEvent
  | KingdomPoolClearedEvent
  | TurnActionUsageResetEvent
  | SupplyStatusResetEvent
  | CleanupCompletedEvent
  | AttackDeclaredEvent
  | CombatCalculatedEvent
  | LandDamagedEvent
  | LaneDefeatedEvent
  | VictoryBannerAwardedEvent
  | LaneCommittedEvent
  | CommanderSpentEvent
  | MatchFinishedEvent;

interface BaseGameState {
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
  readonly matchId: string;
  readonly seed: string;
  readonly random: SeededRandomState;
  readonly turnNumber: number;
  readonly activePlayerId: PlayerId;
  readonly phase: GamePhase;
  readonly players: Readonly<Record<PlayerId, PlayerState>>;
  readonly lanes: Readonly<Record<LaneId, LaneState>>;
  readonly actionLog: readonly GameActionRecord[];
  readonly eventQueue: readonly GameEvent[];
  readonly status: MatchStatus;
}

export interface GameStateV1 extends BaseGameState {
  readonly schemaVersion: 1;
}

export interface GameStateV2 extends BaseGameState {
  readonly schemaVersion: 2;
  readonly catalog: EngineCardCatalogV1;
  readonly cardInstances: Readonly<Record<CardInstanceId, CardInstanceRecord>>;
  readonly setup: MatchSetupMetadata;
  readonly firstTurnDrawStatus: Readonly<Record<PlayerId, FirstTurnDrawStatus>>;
}

export interface GameStateV3 extends BaseGameState {
  readonly schemaVersion: 3;
  readonly catalog: EngineCardCatalogV2 | EngineCardCatalogV3;
  readonly cardInstances: Readonly<Record<CardInstanceId, CardInstanceRecord>>;
  readonly setup: MatchSetupMetadata;
  readonly firstTurnDrawStatus: Readonly<Record<PlayerId, FirstTurnDrawStatus>>;
  readonly supplyStatus: SupplyStatus;
}

/**
 * The versioned current Core Alpha state.  V1–V3 remain historical formats;
 * never infer this revision's territory or warning state for those snapshots.
 */
export interface GameStateV4 extends BaseGameState {
  readonly schemaVersion: 4;
  readonly catalog: EngineCardCatalogV2 | EngineCardCatalogV3;
  readonly cardInstances: Readonly<Record<CardInstanceId, CardInstanceRecord>>;
  readonly setup: MatchSetupMetadata;
  readonly firstTurnDrawStatus: Readonly<Record<PlayerId, FirstTurnDrawStatus>>;
  readonly supplyStatus: SupplyStatus;
  readonly kingdomProgress: Readonly<Record<PlayerId, KingdomProgress>>;
  readonly noTerritoryWarning: NoTerritoryWarning | null;
}

/** Current public-beta state; historical V1-V4 formats remain frozen. */
export interface GameStateV5 extends BaseGameState {
  readonly schemaVersion: 5;
  readonly rulesVersion: typeof PUBLIC_BETA_RULES_VERSION;
  readonly catalog: EngineCardCatalogV4;
  readonly cardInstances: Readonly<Record<CardInstanceId, CardInstanceRecord>>;
  readonly setup: MatchSetupMetadata;
  readonly firstTurnDrawStatus: Readonly<Record<PlayerId, FirstTurnDrawStatus>>;
  readonly supplyStatus: SupplyStatus;
  readonly kingdomProgress: Readonly<Record<PlayerId, KingdomProgress>>;
  readonly noTerritoryWarning: NoTerritoryWarning | null;
}

export type SetupGameState = GameStateV2 | GameStateV3 | GameStateV4 | GameStateV5;
export type CurrentCoreAlphaGameState = GameStateV3 | GameStateV4 | GameStateV5;
export type GameState = GameStateV1 | SetupGameState;

export interface EngineContext {
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
}

export type ActionValidationCode =
  | "ACTION_NOT_IMPLEMENTED"
  | "COMMANDER_DEPLOYED_THIS_TURN"
  | "COMMANDER_REPLACEMENT_INSTANCE_MISMATCH"
  | "COMMANDER_SPENT"
  | "CONTEXT_VERSION_MISMATCH"
  | "CARD_DEFINITION_NOT_FOUND"
  | "CARD_INSTANCE_NOT_FOUND"
  | "CARD_NOT_IN_HAND"
  | "CARD_NOT_LAND"
  | "CARD_NOT_ARMY"
  | "CARD_NOT_COMMANDER"
  | "CARD_NOT_EVENT"
  | "CARD_NOT_OWNED_BY_PLAYER"
  | "FOUNDING_PRIORITY_REQUIRED"
  | "INVALID_STATE"
  | "LAND_ACTION_ALREADY_USED"
  | "LAND_ALREADY_STAGE_THREE"
  | "LAND_DEVELOPMENT_MISMATCH"
  | "LAND_TYPE_MISMATCH"
  | "LANE_NOT_ACTIVE"
  | "MATCH_FINISHED"
  | "MISSING_ATTACKING_COMMANDER"
  | "MISSING_ATTACKING_LAND"
  | "NO_TARGET_LAND"
  | "REPLAY_SETUP_MISMATCH"
  | "KINGDOM_POOL_NOT_EMPTY"
  | "SUPPLY_UPKEEP_UNRESOLVED"
  | "SUPPLY_NOT_AWAITING_DISCARD"
  | "SUPPLY_LANE_NOT_UNSUSTAINABLE"
  | "ARMY_NOT_STATIONED_IN_LANE"
  | "ARMY_CARD_LIMIT_REACHED"
  | "COMMANDER_NOT_STATIONED_IN_LANE"
  | "COMMANDER_CAPACITY_EXCEEDED"
  | "DEPLOYMENT_ACTION_ALREADY_USED"
  | "EVENT_LIMIT_REACHED"
  | "INSUFFICIENT_KINGDOM_POOL"
  | "TARGET_LANE_ALREADY_HAS_COMMANDER"
  | "TARGET_LANE_HAS_NO_COMMANDER"
  | "TARGET_LANE_HAS_NO_LAND"
  | "TARGET_LANE_NOT_ACTIVE"
  | "TARGET_LANE_NOT_EMPTY"
  | "WRONG_PHASE"
  | "WRONG_PLAYER";

export type MatchSetupFailureCode =
  | "INVALID_MATCH_SETUP"
  | "INVALID_DECK"
  | "CATALOG_HASH_MISMATCH"
  | "CARD_DEFINITION_NOT_FOUND"
  | "DUPLICATE_CARD_INSTANCE_ID"
  | "OPENING_HAND_REDRAW_LIMIT_EXCEEDED";

export interface MatchSetupFailure {
  readonly code: MatchSetupFailureCode;
  readonly message: string;
  readonly path: string | null;
  readonly details: Readonly<Record<string, string | number | boolean | null>>;
}

export interface ActionValidationFailure {
  readonly code: ActionValidationCode;
  readonly message: string;
  readonly path: string | null;
  readonly details: Readonly<Record<string, string | number | boolean | null>>;
}

export interface ValidActionResult {
  readonly valid: true;
}

export interface InvalidActionResult {
  readonly valid: false;
  readonly failure: ActionValidationFailure;
}

export type ActionValidationResult = ValidActionResult | InvalidActionResult;

export interface AppliedActionResult {
  readonly ok: true;
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

export interface RejectedActionResult {
  readonly ok: false;
  /** The exact original state reference; failed actions never partially apply. */
  readonly state: GameState;
  readonly events: readonly [];
  readonly failure: ActionValidationFailure;
}

export type ApplyActionResult = AppliedActionResult | RejectedActionResult;

export interface CreatedMatchResult {
  readonly ok: true;
  readonly state: SetupGameState;
  readonly events: readonly GameEvent[];
  readonly initialStateHash: string;
  readonly initialRandomState: SeededRandomState;
  readonly finalRandomState: SeededRandomState;
  readonly startingPlayerId: PlayerId;
  readonly gameplayCatalogHash: string;
}

export interface RejectedMatchSetupResult {
  readonly ok: false;
  readonly failure: MatchSetupFailure;
}

export type CreateMatchResult = CreatedMatchResult | RejectedMatchSetupResult;

export interface ReplayDefinition {
  readonly replayVersion: 1;
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
  readonly initialState: GameState;
  readonly actions: readonly GameAction[];
}

export interface ReplaySetupVerification {
  readonly gameplayCatalogHash: string;
  readonly initialRandomState: SeededRandomState;
  readonly finalRandomState: SeededRandomState;
  readonly initialStateHash: string;
}

export interface ReplayDefinitionV2 {
  readonly replayVersion: 2;
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
  readonly setup: MatchSetupInput & { readonly catalog: EngineCardCatalogV1 };
  readonly setupVerification: ReplaySetupVerification;
  readonly actions: readonly GameAction[];
}

export interface ReplayDefinitionV3 {
  readonly replayVersion: 3;
  readonly rulesVersion: string;
  readonly cardDataVersion: string;
  readonly setup: MatchSetupInput & {
    readonly catalog: EngineCardCatalogV2 | EngineCardCatalogV3;
  };
  readonly setupVerification: ReplaySetupVerification;
  readonly actions: readonly GameAction[];
}

export interface ReplayDefinitionV4 {
  readonly replayVersion: 4;
  readonly rulesVersion: typeof FLANK_KINGDOM_COMPLETE_RULES_VERSION;
  readonly cardDataVersion: string;
  readonly setup: MatchSetupInput & {
    readonly catalog: EngineCardCatalogV2 | EngineCardCatalogV3;
  };
  readonly setupVerification: ReplaySetupVerification;
  readonly actions: readonly GameAction[];
}

export interface ReplayDefinitionV5 {
  readonly replayVersion: 5;
  readonly rulesVersion: typeof PUBLIC_BETA_RULES_VERSION;
  readonly cardDataVersion: string;
  readonly setup: MatchSetupInput & {
    readonly catalog: EngineCardCatalogV4;
  };
  readonly setupVerification: ReplaySetupVerification;
  readonly actions: readonly GameAction[];
}

export type AnyReplayDefinition =
  | ReplayDefinition
  | ReplayDefinitionV2
  | ReplayDefinitionV3
  | ReplayDefinitionV4
  | ReplayDefinitionV5;

export interface SuccessfulReplayResult {
  readonly ok: true;
  readonly state: GameState;
  readonly events: readonly GameEvent[];
  readonly stateHashes: readonly string[];
}

export interface FailedReplayResult {
  readonly ok: false;
  readonly state: GameState;
  readonly events: readonly GameEvent[];
  readonly stateHashes: readonly string[];
  readonly failedActionIndex: number;
  readonly failure: ActionValidationFailure;
}

export type ReplayResult = SuccessfulReplayResult | FailedReplayResult;

export interface SuccessfulReplayV2Result extends SuccessfulReplayResult {
  readonly state: GameStateV2;
}

export interface FailedReplaySetupResult {
  readonly ok: false;
  readonly state: GameStateV2 | null;
  readonly events: readonly GameEvent[];
  readonly stateHashes: readonly string[];
  readonly failedActionIndex: -1;
  readonly failure: MatchSetupFailure | ActionValidationFailure;
}

export type ReplayV2Result =
  SuccessfulReplayV2Result | FailedReplayResult | FailedReplaySetupResult;

export interface SuccessfulReplayV3Result extends SuccessfulReplayResult {
  readonly state: GameStateV3;
}

export interface SuccessfulReplayV4Result extends SuccessfulReplayResult {
  readonly state: GameStateV4;
}

export interface FailedReplayV3SetupResult {
  readonly ok: false;
  readonly state: GameStateV3 | null;
  readonly events: readonly GameEvent[];
  readonly stateHashes: readonly string[];
  readonly failedActionIndex: -1;
  readonly failure: MatchSetupFailure | ActionValidationFailure;
}

export type ReplayV3Result =
  SuccessfulReplayV3Result | FailedReplayResult | FailedReplayV3SetupResult;

export interface FailedReplayV4SetupResult {
  readonly ok: false;
  readonly state: GameStateV4 | null;
  readonly events: readonly GameEvent[];
  readonly stateHashes: readonly string[];
  readonly failedActionIndex: -1;
  readonly failure: MatchSetupFailure | ActionValidationFailure;
}

export type ReplayV4Result =
  SuccessfulReplayV4Result | FailedReplayResult | FailedReplayV4SetupResult;

export interface SuccessfulReplayV5Result extends SuccessfulReplayResult {
  readonly state: GameStateV5;
}

export interface FailedReplayV5SetupResult {
  readonly ok: false;
  readonly state: GameStateV5 | null;
  readonly events: readonly GameEvent[];
  readonly stateHashes: readonly string[];
  readonly failedActionIndex: -1;
  readonly failure: MatchSetupFailure | ActionValidationFailure;
}

export type ReplayV5Result =
  SuccessfulReplayV5Result | FailedReplayResult | FailedReplayV5SetupResult;

export function getOpponentPlayerId(playerId: PlayerId): PlayerId {
  return playerId === "player-1" ? "player-2" : "player-1";
}
